// Project 100K: free-shipping progress bar. One custom element, used in the cart popup, cart page and drawer.
// Every bar animates from the last total the shopper saw (sessionStorage) and re-syncs after any cart change.
(() => {
  if (customElements.get('p100k-free-ship-bar')) return;

  const KEY = 'p100k-fsb-total';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const COLORS = ['#FA5000', '#FFB000', '#FF7A1F', '#2B2B2B', '#FFFFFF'];

  const lastSeen = () => {
    try { const v = sessionStorage.getItem(KEY); return v === null ? null : Number(v); } catch (e) { return null; }
  };
  const remember = (total) => { try { sessionStorage.setItem(KEY, String(total)); } catch (e) { /* private mode */ } };
  const money = (cents) => {
    const r = cents % 100 ? (cents / 100).toFixed(2) : String(cents / 100);
    return 'R' + r.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  class FreeShipBar extends HTMLElement {
    connectedCallback() {
      this.threshold = Number(this.dataset.threshold) || 65000;
      const total = Number(this.dataset.total) || 0;
      const from = lastSeen() ?? 0;
      // Paint the previous state without animating, then animate to the real one.
      this.classList.add('no-anim');
      this.paint(from);
      void this.offsetWidth;
      this.classList.remove('no-anim');
      requestAnimationFrame(() => this.update(total, from));
    }

    update(total, from = this.total) {
      this.paint(total);
      if (from !== undefined && from < this.threshold && total >= this.threshold) this.celebrate();
      remember(total);
    }

    paint(total) {
      this.total = total;
      const left = this.threshold - total;
      const unlocked = left <= 0;
      const pct = Math.min(100, (total / this.threshold) * 100);
      this.hidden = total <= 0;
      this.style.setProperty('--fsb-pct', pct + '%');
      this.classList.toggle('is-unlocked', unlocked);
      const icon = this.querySelector('.p100k-fsb__icon');
      if (icon) {
        icon.classList.toggle('ph-confetti', unlocked);
        icon.classList.toggle('ph-truck', !unlocked);
      }
      const text = this.querySelector('.p100k-fsb__text');
      if (text) {
        text.innerHTML = unlocked
          ? '<strong>Free shipping unlocked!</strong> Your biltong ships on us.'
          : `You're <strong>${money(left)}</strong> away from <strong>free shipping</strong>`;
      }
    }

    celebrate() {
      if (reduceMotion) return;
      this.classList.remove('is-pop');
      void this.offsetWidth;
      this.classList.add('is-pop');
      for (let i = 0; i < 28; i++) {
        const bit = document.createElement('span');
        bit.className = 'p100k-fsb__confetti';
        bit.style.background = COLORS[i % COLORS.length];
        this.appendChild(bit);
        const angle = Math.random() * Math.PI * 2;
        const dist = 50 + Math.random() * 110;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist * 0.6 - 30;
        bit.animate(
          [
            { transform: 'translate(-50%, -50%) rotate(0deg)', opacity: 1 },
            { transform: `translate(${dx}px, ${dy}px) rotate(${Math.random() * 720 - 360}deg)`, opacity: 1, offset: 0.7 },
            { transform: `translate(${dx * 1.1}px, ${dy + 60}px) rotate(${Math.random() * 720}deg)`, opacity: 0 },
          ],
          { duration: 1100 + Math.random() * 600, easing: 'cubic-bezier(.2,.7,.3,1)' }
        ).onfinish = () => bit.remove();
      }
    }
  }
  customElements.define('p100k-free-ship-bar', FreeShipBar);

  // Re-sync every bar after the theme talks to the cart (add, change, remove, update).
  const cartUrl = () => ((window.routes && window.routes.cart_url) || '/cart') + '.js';
  const refresh = () =>
    fetch(cartUrl(), { headers: { accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => document.querySelectorAll('p100k-free-ship-bar').forEach((bar) => bar.update(cart.total_price)))
      .catch(() => {});

  const originalFetch = window.fetch;
  window.fetch = function (input) {
    const request = originalFetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      if (/\/cart\/(add|change|update|clear)/.test(url)) request.then(() => setTimeout(refresh, 0)).catch(() => {});
    } catch (e) { /* never break the theme's own fetch */ }
    return request;
  };
})();
